import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { ChangeLogMonitorService } from '../../../services/change-log-monitor.service';
import { GlobalService } from '../../../../global/services/global.service';
import { WorkspaceService } from '../../../../workspace/workspace.service';
import { AppService } from '../../../../app.service';
import {
  buildHeaderDataShell,
  rowPayloadFromActiveScreen,
  gridPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-rerun-pdf-generation',
    templateUrl: './rerun-pdf-generation.component.html',
    styleUrls: ['./rerun-pdf-generation.component.scss', '../../../slt-dashboard.styles.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class RerunPdfGenerationComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // STANDARD COMPONENT PROPERTIES
  modalTitle = "Re Run PDF Generation";
  minimise = false;
  isSaveClicked = false;

  // Component-specific properties
  saveloader = false;
  job_id: string[] = [];

  // Data properties
  baseURLs: any = {};
  headerData: any = {};
  rowData: any = {};
  filterParams: any = {};
  gridData: any[] = [];
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  globalService: any = {};

  private readonly subs = new Subscription();

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private changeLogMonitorService: ChangeLogMonitorService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.headerData = header;
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.globalService = header;
    const rowPayload = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rowPayload) ? (rowPayload[0] ?? {}) : (rowPayload ?? {});
    const g = gridPayloadFromActiveScreen(activeScreen);
    this.gridData = Array.isArray(g) ? g : g != null ? [g] : [];
    if (this.gridData.length) {
      this.processGridData();
    }
    this.cdr.markForCheck();
  }

  ngOnInit(): void {
    console.log('RerunPdfGeneration - Component initialized');
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  init() {
    try {
      console.log('RerunPdfGeneration - init() called with rowData:', this.rowData);
      console.log('RerunPdfGeneration - gridData:', this.gridData);
      console.log('RerunPdfGeneration - job_id:', this.job_id);
      // Initial setup if needed
    } catch (e) {
      console.error('Error in RerunPdfGeneration init:', e);
    }
  }

  processGridData() {
    console.log('RerunPdfGeneration - processGridData() called with:', this.gridData);
    // Process grid data to extract job IDs
    this.job_id = [];

    this.gridData.forEach((item: any) => {
      const data = item.data || item;
      console.log('RerunPdfGeneration - Processing item:', data);
      this.job_id.push(data.JOB_ID);
    });

    console.log('RerunPdfGeneration - Extracted job_id:', this.job_id);
    this.cdr.detectChanges();
  }

  removeTag(index: number) {
    this.job_id.splice(index, 1);
    this.cdr.detectChanges();
  }

  regenerateProcess() {
    this.isSaveClicked = true;
    this.saveloader = true;
    this.cdr.detectChanges();

    const gp = this.globalService?.globalParams || {};

    // Build params similar to AngularJS - merge globalParams with specific params
    const params = {
      ...gp,
      job_id: this.job_id.toString(),
      xml_pdf_flag: "PDF"
    };

    console.log('RerunPdfGeneration - Calling API with params:', params);

    this.changeLogMonitorService.saveRerunPdfGeneration(
      this.GENERAL_CONFIG.base_url,
      params
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.saveloader = false;

        if (response.callSuccess == "1") {
          this._snackBar.open('Re run PDF Generation process executed successfully', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        } else {
          // AngularJS accesses data.data.message, but service returns response.data
          // So we need to check both response.message and response.data?.message
          const errorMessage = response.message || response.data?.message || 'Error occurred while re-running PDF generation';
          this._snackBar.open(errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        }

        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error re-running PDF generation:', error);
        this.isSaveClicked = false;
        this.saveloader = false;
        this._snackBar.open('Error occurred while re-running PDF generation. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }
}
